/**
 * Unified EmptyState Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { cn } from "../utils/cn";
import type { EmptyStateProps } from "./EmptyState.types";

const sizeStyles: Record<string, string> = {
  sm: "py-4 px-4",
  md: "py-8 px-6",
  lg: "py-12 px-8",
};

export function EmptyState({
  title,
  description,
  icon,
  actionLabel,
  onAction,
  actionHref,
  action,
  size = "md",
  variant = "default",
  className,
}: EmptyStateProps) {
  const displayTitle = title;

  const content = (
    <>
      {icon && (
        <div className="mb-4 text-gray-400" aria-hidden="true">
          {icon}
        </div>
      )}
      <h3 className={cn("font-medium text-gray-900 dark:text-gray-100", size === "sm" ? "text-sm" : "text-base")}>
        {displayTitle}
      </h3>
      {/* `dark:text-gray-400` is REQUIRED. The gray ramp is STATIC on web
          (`inject-runtime-tokens` writes pre-resolved literals that are
          identical in `:root` and `html.dark`; the inverting ramp in
          tokens-apps `colors.css` is mobile-only), so a bare `text-gray-500`
          stays #64748b in dark mode = 3.07:1 on the dark card (#1e293b), a
          WCAG 1.4.3 FAIL. #94a3b8 measures 5.71:1. Callers had been working
          around this by omitting `description` entirely.
          Light is `text-gray-600` (not -500): gray-500 on the bordered
          variant's gray-100 surface is 4.34:1 (Ladle a11y gate 2026-10-18).
          The CTA uses brand-700 in dark too — white on brand-500 is ~2.7:1. */}
      {description && (
        <p className="mt-1 max-w-sm text-center text-sm text-gray-600 dark:text-gray-400">{description}</p>
      )}

      {/* Action slot (arbitrary ReactNode) takes precedence */}
      {action && <div className="mt-4">{action}</div>}

      {/* CTA button/link (only when no action slot) */}
      {!action && actionLabel && actionHref && (
        <a
          href={actionHref}
          className="mt-4 inline-block rounded-md bg-brand px-4 py-2 text-sm font-medium text-white no-underline hover:bg-brand-hover dark:bg-brand-700 dark:hover:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus"
        >
          {actionLabel}
        </a>
      )}
      {!action && actionLabel && onAction && !actionHref && (
        <button
          type="button"
          onClick={onAction}
          className="mt-4 inline-block rounded-md border-none bg-brand px-4 py-2 text-sm font-medium text-white cursor-pointer hover:bg-brand-hover dark:bg-brand-700 dark:hover:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus"
        >
          {actionLabel}
        </button>
      )}
    </>
  );

  if (variant === "bordered") {
    return (
      <section
        className={cn(
          // Literal gray ramp — do NOT use `border-[var(--color-border-default)]` or
          // `bg-surface-subtle`. Those emit `var(--color-*)` with NO fallback and are
          // stripped from the React SPA apps' Tailwind v4 build.
          "flex min-h-[200px] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800",
          sizeStyles[size],
          className
        )}
        aria-label={displayTitle}
      >
        {content}
      </section>
    );
  }

  return (
    <section
      className={cn("flex flex-col items-center justify-center text-center", sizeStyles[size], className)}
      aria-label={displayTitle}
    >
      {content}
    </section>
  );
}

EmptyState.displayName = "EmptyState";
